(ns shared.core.components.recharts.util
  (:require ["recharts" :refer [LineChart BarChart PieChart ComposedChart AreaChart ScatterChart Scatter ZAxis
                                RadarChart PolarGrid PolarAngleAxis PolarRadiusAxis Radar
                                Sankey Rectangle Layer Line ReferenceLine ReferenceArea ReferenceDot Bar Area Pie Cell XAxis YAxis
                                CartesianGrid Tooltip Legend ResponsiveContainer]]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.time :as time]
            [cljs-time.format :as t.f]
            [shared.core.util :as util]
            [shared.core.constants :as constants]
            [clojure.string :as s]))

(def re-line-chart (re/adapt-react-class LineChart))
(def re-line (re/adapt-react-class Line))
(def re-reference-line (re/adapt-react-class ReferenceLine))
(def re-reference-area (re/adapt-react-class ReferenceArea))
(def re-reference-dot (re/adapt-react-class ReferenceDot))
(def re-bar-chart (re/adapt-react-class BarChart))
(def re-bar (re/adapt-react-class Bar))
(def re-composed-chart (re/adapt-react-class ComposedChart))
(def re-area-chart (re/adapt-react-class AreaChart))
(def re-area (re/adapt-react-class Area))
(def re-legend (re/adapt-react-class Legend))
(def re-scatter-chart (re/adapt-react-class ScatterChart))
(def re-scatter (re/adapt-react-class Scatter))
(def re-z-axis (re/adapt-react-class ZAxis))
(def re-radar-chart (re/adapt-react-class RadarChart))
(def re-polar-grid (re/adapt-react-class PolarGrid))
(def re-polar-angle-axis (re/adapt-react-class PolarAngleAxis))
(def re-polar-radius-axis (re/adapt-react-class PolarRadiusAxis))
(def re-radar (re/adapt-react-class Radar))
(def re-pie-chart (re/adapt-react-class PieChart))
(def re-pie (re/adapt-react-class Pie))
(def re-cell (re/adapt-react-class Cell))
(def re-x-axis (re/adapt-react-class XAxis))
(def re-y-axis (re/adapt-react-class YAxis))
(def re-cartesian-grid (re/adapt-react-class CartesianGrid))
(def re-tooltip (re/adapt-react-class Tooltip))
(def re-responsive-container (re/adapt-react-class ResponsiveContainer))
(def re-sankey (re/adapt-react-class Sankey))
(def re-rectangle (re/adapt-react-class Rectangle))
(def re-layer (re/adapt-react-class Layer))

(style/add-css [".recharts-wrapper>.recharts-surface"
                {:overflow :visible}])

(defn color-from-string [in-string & {:keys [default opacity] :or {default ""}}]
  (some-> (or in-string default) (util/str->hex) (util/hex->rgba (or opacity 0.5))))

(defn point-double-click-handler
  "A chart-level `onDoubleClick` for the line, area and radar charts, which recharts fires
   anywhere in the plot with the hovered x (or spoke) as its active point. Calls
   `on-double-click` like the bar and pie charts do — with the event and
   `{:payload <that point's row> :series-key <the hovered series, if any>}`."
  [on-double-click hovered-key*]
  (fn [^js state e]
    (when-let [^js point (some-> state (.-activePayload) (first))]
      (on-double-click e {:payload (js->clj (.-payload point) :keywordize-keys true)
                          :series-key (some-> hovered-key* (deref))}))))

(defonce ^:private measure-ctx
         (delay
           (some-> js/document
                   (.createElement "canvas")
                   (.getContext "2d"))))

(defn measure-text-width
  "Width in px of `text` rendered with the given canvas font shorthand.
   Falls back to a rough per-character estimate when no canvas is available."
  [text font]
  (let [text (str text)]
    (if-let [ctx @measure-ctx]
      (do (set! (.-font ctx) font)
          (.-width (.measureText ctx text)))
      (* 7 (count text)))))

(def tick-font (str style/font-regular-weight " 11px " style/roboto-font-family))

(defn- widest-label [labels]
  (->> labels
       (remove nil?)
       (map #(measure-text-width % tick-font))
       (reduce max 0)))

(defn fit-y-axis-width
  "Y-axis width sized to fit its widest tick label — the cartesian analog of the
   sankey chart's label-fit left margin."
  [labels]
  (js/Math.ceil (+ (widest-label labels) 16)))

(def legend-font (str style/font-regular-weight " 12px " style/roboto-font-family))

(defn legend-item-styles
  "The chip, swatch and text styles every chart legend item is built from — shared by
   `standalone-legend` and the pie legend (`legend/legend-item`) so the two can't drift
   apart. A function, not a def, because `alc-color` reads the live theme.

   `legend-item-chrome` is what one chip costs horizontally besides its text."
  []
  {:chip {:background-color (style/alc-color "Background/card_hex")
          :border-radius 3
          :display :flex
          :align-items :center
          :gap 6
          :padding 4
          :margin 2}
   :swatch {:width 12
            :height 12
            :border-radius 2
            :flex-shrink 0}
   :text {:color (style/alc-color "Text/primary")
          :font-family style/roboto-font-family
          :font-size 12
          :line-height 1.2}})

(def legend-item-chrome
  "Margin + padding + swatch + gaps of one legend chip (see `legend-item-styles`)."
  (+ 4 8 12 6))

(defn fit-legend-width
  "Width the pie/donut legend needs to render `legend-data` (see `prepare-legend-data`)
   on one row per item: its widest name plus the chip's chrome, the gap before the
   percentage and the percentage itself."
  [legend-data]
  (let [widest-name (->> legend-data
                         (map #(measure-text-width (:name %) legend-font))
                         (reduce max 0))]
    (js/Math.ceil (+ widest-name
                     legend-item-chrome
                     12
                     (measure-text-width "100.0%" legend-font)))))

(def x-scrollbar-allowance
  "Extra height on a horizontally scrollable chart container so the scrollbar
   renders below the x-axis labels instead of over them."
  16)

(defn scroll-frame
  "Horizontally scrollable frame around a cartesian chart. With `fill-width?` the chart
   stretches to the container but never below `min-width`, scrolling past that; otherwise
   `chart` must carry its own fixed width and sits at that size, scrolling if it's wider.
   Remaining `attrs` (`:class`, `:on-click`, ...) go on the frame."
  [{:keys [fill-width? min-width height style] :as attrs} chart]
  (let [scrolls? (or (not fill-width?) min-width)]
    [:div (merge (dissoc attrs :fill-width? :min-width :height :style)
                 {:style (merge {:width (if fill-width? "100%" :auto)
                                 :max-width "100%"
                                 :height (cond-> height
                                                 scrolls?
                                                 (+ x-scrollbar-allowance))
                                 :overflow-x :auto
                                 :overflow-y :hidden}
                                style)})
     (if fill-width?
       [re-responsive-container (cond-> {:width "100%"
                                         :height height}
                                        min-width
                                        (assoc :min-width min-width))
        chart]
       chart)]))

(defn tick-label
  "A numeric tick's text; `:percent?` on the axis config labels it as a percent."
  [axis-config v]
  (cond-> (util/readable-number v)
          (:percent? axis-config) (str "%")))

(defn custom-rotated-x-tick [props {:keys [description-fn value-transf-fn percent?] :as axis-config}]
  (let [{:keys [x y angle payload] :as args} (js->clj props :keywordize-keys true)
        value-str (cond
                    value-transf-fn (value-transf-fn payload)
                    (and percent? (number? (:value payload))) (tick-label axis-config (:value payload))
                    :else (:value payload))
        value-description (when description-fn (description-fn args))]
    (re/as-element
      [:g {:transform (str "translate(" x "," y ")"
                           (when angle
                             (str " rotate(" angle ")")))}
       [:text {:x 0
               :y 0
               :dy 16
               :text-anchor (cond
                              (pos? angle) "start"
                              (neg? angle) "end"
                              :else "middle")
               :font-style :italic
               :font-family style/roboto-font-family
               :fill (style/alc-color "Text/primary")
               :font-size 11}
        value-str]
       (when (not-empty value-description)
         [:title value-description])])))

(defn custom-y-tick [props {:keys [value-transf-fn orientation] :as axis-config}]
  (let [{:keys [x y payload]} (js->clj props :keywordize-keys true)
        right? (= orientation :right)]
    (re/as-element
      [:text {:x (if right? (+ x 8) (- x 8))
              :y y
              :text-anchor (if right? "start" "end")
              :font-family style/roboto-font-family
              :fill (style/alc-color "Text/primary")
              :font-size 11}
       (if value-transf-fn
         (value-transf-fn payload)
         (try
           (tick-label axis-config (:value payload))
           (catch :default _
             (:value payload))))])))

(defn angle-for-value
  [value total start-angle end-angle]
  (let [span (- end-angle start-angle)
        percent (/ value total)]
    (+ start-angle (* span percent))))

(defn deg->rad [deg] (/ (* Math/PI deg) 180))

(defn fit-x-axis-height
  "X-axis height sized to fit its tick labels rotated by `angle` degrees
   (0 = horizontal) — the cartesian analog of the sankey chart's label-fit
   bottom margin."
  [labels angle]
  (let [angle (or angle 0)
        extent (if (zero? angle)
                 16
                 (* (widest-label labels) (Math/sin (deg->rad (Math/abs angle)))))]
    (js/Math.ceil (+ extent 24))))

(defn fit-x-label-left-overflow
  "How far rotated x-axis tick labels reach to the LEFT of their own tick — the
   horizontal companion to `fit-x-axis-height`. 0 for horizontal labels, which are
   centred on their tick rather than anchored at their end."
  [labels angle]
  (if (or (nil? angle) (zero? angle))
    0
    (js/Math.ceil (* (widest-label labels) (Math/cos (deg->rad (Math/abs angle)))))))

(defn position-for-angle
  ([cx cy radius angle]
   (position-for-angle cx cy radius angle 0))
  ([cx cy radius angle delta]
   ;; Calculate the position based on the center (cx, cy), radius, angle, and optional delta
   ;; Delta is used to adjust the position perpendicular to the angle
   (let [rad (deg->rad angle)
         rad-perp (deg->rad (+ angle 90))]
     {:x (+ cx (* radius (Math/cos rad))
            (* delta (Math/cos rad-perp)))
      :y (- cy (* radius (Math/sin rad))
            (* delta (Math/sin rad-perp)))})))

(defn sankey-chart-height
  "Container height that actually fits the sankey's tallest column.
   Recharts stacks a column of n nodes needing (n-1)*node-padding of gap plus
   the node thicknesses, so the height must grow with both the node count and
   node-padding — otherwise the laid-out surface (which renders overflow:visible)
   spills off the card instead of the card growing to create scroll."
  [{:keys [nodes node-padding margin]
    :or {node-padding 20}}]
  (let [node-count (max (count (filter :isSource nodes))
                        (count (remove :isSource nodes)))
        {:keys [top bottom] :or {top 40 bottom 40}} margin
        per-node-thickness 10]
    (+ top bottom (* node-count (+ node-padding per-node-thickness)))))

(defn needle-polygon-points
  [cx cy radius angle width]
  (let [half-width (/ width 2)
        inner-radius (* 0.64 radius)
        outer-radius (- radius (/ width 2))
        bottom-left (position-for-angle cx cy inner-radius angle half-width)
        bottom-right (position-for-angle cx cy inner-radius angle (- half-width))
        top-left (position-for-angle cx cy outer-radius angle half-width)
        top-right (position-for-angle cx cy outer-radius angle (- half-width))
        tip (position-for-angle cx cy radius angle)]
    (str (:x bottom-left) "," (:y bottom-left) " "
         (:x bottom-right) "," (:y bottom-right) " "
         (:x top-right) "," (:y top-right) " "
         (:x tip) "," (:y tip) " "
         (:x top-left) "," (:y top-left))))

(defn calculate-total
  "Calculates the total value from all data items"
  [data & {:keys [value-key]}]
  (reduce (fn [sum item] (+ sum (if value-key
                                  (get item value-key)
                                  (or (:value item) (get item "value") 0))))
          0
          data))

(defn calculate-percentage
  [value total]
  (if (zero? total)
    0
    (* (/ value total) 100)))

(defn add-percentages
  "Adds percentage field to each data item"
  [data]
  (let [total (calculate-total data)]
    (mapv (fn [item]
            (assoc item :percentage (calculate-percentage (:value item) (or (:total item) total))))
          data)))

(defn prepare-legend-data
  "Prepares legend data with categories and percentages.

   Returns vector of maps with :name, :value, :percentage, :fill"
  [{:keys [data name-key value-key total] :or {name-key :name}}]
  (let [data-with-pct (add-percentages data)]
    (mapv (fn [item]
            (let [item-name (get item name-key)
                  item-value (if value-key
                               (or (get item value-key) (get item (keyword value-key)))
                               (or (:value item) (get item "value")))]
              {:name item-name
               :value item-value
               :percentage (or (when (some-> (:percentage item) (pos?))
                                 (:percentage item))
                               (when (and (some-> item-value (pos?)) (some-> total (pos?)))
                                 (calculate-percentage item-value total))
                               0)
               :fill (or (:fill item) (color-from-string (get item name-key)))}))
          data-with-pct)))

(defn format-date-for-chart
  "Formats a date string to 'Nov 5, Wed' format"
  [date-str]
  (try
    (some-> date-str (time/reformat-date-str "MMM d, EEE"))
    (catch :default _
      date-str)))

(defn extract-date
  "Extracts just the date portion from a timestamp string"
  [timestamp-str]
  (when timestamp-str
    (some-> timestamp-str (s/split #" ") (first))))

(defn extract-hour-of-day
  "Extracts just the hour of day (0-23) from a timestamp string"
  [timestamp-str]
  (try
    (some-> timestamp-str (time/reformat-date-str "H"))
    (catch :default _
      timestamp-str)))

(defn get-day-of-week
  "Gets the day of the week from a timestamp"
  [timestamp-str]
  (when timestamp-str
    (try
      (let [date (js/Date. timestamp-str)
            day-num (.getDay date)]
        day-num)
      (catch :default _
        nil))))

(defn format-day-of-week
  "Formats a day number (0-6) to day name"
  [day-num]
  (let [days ["Sunday" "Monday" "Tuesday" "Wednesday" "Thursday" "Friday" "Saturday"]]
    (get days day-num)))

(defn item->key
  "Converts an item map to a key string based on provided key-fn.
   Default format: 'field1.field2' or custom format from key-fn"
  [item key-fn]
  (when item
    (key-fn item)))

(defn analyze-by-time-period
  "Generic function to analyze items grouped by a time period.

   Parameters:
   - items: Collection of items to analyze
   - timestamp-key: Keyword to extract timestamp from items (e.g., :conversation-ts)
   - time-extractor-fn: Function to extract time period from timestamp (e.g., extract-date, extract-hour)
   - time-formatter-fn: Function to format time period for display
   - values-key: Keyword to extract values collection from items (e.g., :utterance-tags)
   - item-key-fn: Function to convert an item to a key string
   - item-filter-fn: Optional function to filter items (default: identity)
   - sort-fn: Optional function to convert sort-key to comparable value (default: identity)"
  [items timestamp-key time-extractor-fn time-formatter-fn values-key item-key-fn & {:keys [item-filter-fn sort-fn]
                                                                                     :or {item-filter-fn identity
                                                                                          sort-fn identity}}]
  (when (seq items)
    (let [items-by-period (group-by (if timestamp-key (comp time-extractor-fn timestamp-key) (fn [_] nil)) items)
          period-data (->> items-by-period
                           (map (fn [[period period-items]]
                                  (let [item-counts (->> period-items
                                                         (mapcat values-key)
                                                         (map #(item-key-fn %))
                                                         (filter identity)
                                                         (filter item-filter-fn)
                                                         (frequencies))
                                        formatted-period (time-formatter-fn period)]
                                    (merge {:name (if (or timestamp-key formatted-period)
                                                    formatted-period
                                                    "Total")
                                            :sort-key period
                                            :timestamp period}
                                           item-counts))))
                           (sort-by (comp sort-fn :sort-key))
                           (mapv #(dissoc % :sort-key)))]
      period-data)))

(defn analyze-by-hour
  "Analyzes items by hour of day (0-23), aggregating across all days.
   Groups data by hour of day and only shows hours that have data.

   For example, if you have data from the last 7 days at 7:00 AM, this will aggregate
   all of those 7:00 AM data points into a single bar."
  [items timestamp-key values-key item-key-fn & {:keys [item-filter-fn]
                                                 :or {item-filter-fn identity}}]
  (when (seq items)
    (let [;; Group items by hour of day (0-23)
          items-by-hour (group-by (comp extract-hour-of-day timestamp-key) items)

          ;; Process each hour
          hour-data (->> items-by-hour
                         (map (fn [[hour-num hour-items]]
                                (when (some? hour-num)      ; Only process valid hours
                                  (let [item-counts (->> hour-items
                                                         (mapcat values-key)
                                                         (map #(item-key-fn %))
                                                         (filter identity)
                                                         (filter item-filter-fn)
                                                         (frequencies))
                                        formatted-hour (util/readable-hour-num hour-num)]
                                    (merge {:name formatted-hour
                                            :sort-key (some-> hour-num (util/parse-int))}
                                           item-counts)))))
                         (filter some?)                     ; Remove nil entries
                         ;; Filter out hours with no data (all counts are 0 or missing)
                         (filter #(some (fn [[k v]] (and (not= k :name)
                                                         (not= k :sort-key)
                                                         (number? v)
                                                         (pos? v))) %))
                         ;; Sort by hour number (0-23)
                         (sort-by :sort-key)
                         (mapv #(dissoc % :sort-key)))]
      hour-data)))

(defn analyze-by-date
  "Analyzes items by date using the generic time period analyzer.
   Sorts by actual date value, not date string."
  [items timestamp-key values-key item-key-fn & {:keys [item-filter-fn]
                                                 :or {item-filter-fn identity}}]
  (analyze-by-time-period items timestamp-key extract-date format-date-for-chart
                          values-key item-key-fn
                          :item-filter-fn item-filter-fn
                          :sort-fn (fn [date-str]
                                     (when date-str
                                       (try
                                         (.getTime (js/Date. date-str))
                                         (catch :default _
                                           date-str))))))

(defn analyze-by-day-of-week
  "Analyzes items by day of week using the generic time period analyzer"
  [items timestamp-key values-key item-key-fn & {:keys [item-filter-fn]
                                                 :or {item-filter-fn identity}}]
  (analyze-by-time-period items timestamp-key get-day-of-week format-day-of-week
                          values-key item-key-fn :item-filter-fn item-filter-fn))

(defn get-all-keys
  "Extracts all unique keys from the chart data, excluding :name and :sort-key.
   Optional filter-fn to filter keys."
  [chart-data & {:keys [filter-fn]
                 :or {filter-fn identity}}]
  (->> chart-data
       (mapcat keys)
       (filter #(not= % :name))
       (filter #(not= % :sort-key))
       (filter filter-fn)
       (distinct)
       (sort)
       (vec)))

(defn analyze-by-timestamp-as-pct-of-overall-items [{:keys [items timestamp-key date-extract-fn values-key filter-fn
                                                            group-by-key name-fn sort-fn]}]
  (->> items
       (group-by #(-> % timestamp-key (date-extract-fn)))
       (mapv (fn [[extracted-date timestamp-items]]
               (let [total-items (count timestamp-items)]
                 (assoc (->> timestamp-items
                             (mapcat (fn [ti]
                                       (->> ti
                                            values-key
                                            (#(if filter-fn
                                                (filter filter-fn %)
                                                %))
                                            (group-by group-by-key)
                                            (keys)
                                            (mapv (fn [k] [k 1])))))
                             (group-by first)
                             (map (fn [[k k-items]]
                                    [k (-> (count k-items)
                                           (/ total-items)
                                           (* 100)
                                           (util/to-precision-num :precision 1))]))
                             (into {}))
                   :timestamp extracted-date
                   :name (if name-fn
                           (name-fn extracted-date)
                           extracted-date)))))
       (sort-by (or sort-fn identity))
       (vec)))

(defn build-total-counts [d k]
  (->> d
       (group-by k)
       (map (fn [[k vals]] [k (apply + (map :count vals))]))
       (into {})))

(defn group-and-format-time-grouping-data [{:keys [data count-group-by-key forced-time-key]}]
  (when (not-empty data)
    (let [grouped (group-by :time-grouping data)
          process-grouping (fn [grouping-key time-key sort-fn display-fn]
                             (let [time-key (or forced-time-key time-key)
                                   data (get grouped grouping-key)
                                   total-counts (when count-group-by-key
                                                  (build-total-counts data count-group-by-key))]
                               {:data (->> data
                                           (#(if sort-fn
                                               (sort-by sort-fn %)
                                               %))
                                           (mapv (fn [r]
                                                   (cond-> (assoc r time-key (display-fn (:time-value r)))
                                                           count-group-by-key
                                                           (assoc :total-count (get total-counts (count-group-by-key r)))))))}))
          all (process-grouping "all" :total nil (fn [] "Total"))]
      (cond-> {:hourly (process-grouping "hourly" :hour
                                         #(-> % :time-value (util/parse-int))
                                         util/readable-hour-num)
               :daily (process-grouping "daily" :day
                                        :time-value
                                        (fn [date-str]
                                          (let [parsed-date (t.f/parse (t.f/formatter "yyyy-MM-dd") date-str)]
                                            (t.f/unparse (t.f/formatter "MMM dd, EEE") parsed-date))))
               :day-of-week (process-grouping "day-of-week" :day-of-week
                                              (fn [r]
                                                (get constants/days-of-the-week-map (:time-value r) 7))
                                              identity)}
              (not-empty all)
              (assoc :all all)))))

(defn build-sankey-data [{:keys [source-key source-colors source-config target-key target-colors target-config value-key
                                 data sort-sources sort-sources-by-key? sort-targets sort-targets-by-key?]}]
  (let [get-color (fn [color-map v]
                    (or (when color-map
                          (if (map? color-map)
                            (get color-map v)
                            (color-map v)))
                        (util/str->hex v)))
        sort-sources (or sort-sources
                         (when (and sort-sources-by-key? source-key)
                           (fn [a b]
                             (compare (get a source-key) (get b source-key)))))
        sources (->> data
                     (#(if sort-sources
                         (sort sort-sources %)
                         %))
                     (map source-key)
                     (distinct)
                     (mapv (fn [v]
                             (util/->camelCaseKeywords (merge {:name v
                                                               :is-source true
                                                               :color (get-color source-colors v)
                                                               :width 10
                                                               :stroke (style/alc-color "Background/card_hex")
                                                               :stroke-width 1
                                                               :opacity 1}
                                                              source-config)))))
        sort-targets (or sort-targets
                         (when (and sort-targets-by-key? target-key)
                           (fn [a b]
                             (compare (get a target-key) (get b target-key)))))
        targets (->> data
                     (#(if sort-targets
                         (sort sort-targets %)
                         %))
                     (map target-key)
                     (distinct)
                     (mapv (fn [v]
                             (util/->camelCaseKeywords (merge {:name v
                                                               :color (get-color target-colors v)
                                                               :width 2
                                                               :opacity 0.8}
                                                              target-config)))))
        source-name->index (->> sources
                                (map-indexed (fn [idx source] [(:name source) idx]))
                                (into {}))
        target-name->index (->> targets
                                (map-indexed (fn [idx target] [(:name target) idx]))
                                (into {}))]
    {:nodes (vec (concat sources targets))
     :links (->> data
                 (mapv (fn [r]
                         (let [source (get r source-key)
                               target (get r target-key)
                               value (get r value-key)]
                           {:source (source-name->index source)
                            :target (+ (count sources) (target-name->index target))
                            :value value
                            :color (get-color source-colors source)}))))}))

(defn goal-label
  "A goal line's label: `goal-config`'s `:label`, else `Goal: <goal>` (as a percent
   when `percent?`)."
  [goal goal-config percent?]
  (or (:label goal-config)
      (get goal-config "label")
      (str "Goal: " (if percent?
                      (str (js/Math.round goal) "%")
                      (util/readable-number goal)))))

(defn goal-right-margin
  "Right chart margin that fits a goal `label` drawn past the plot's right edge."
  [label]
  (+ 16 (js/Math.ceil (measure-text-width label tick-font))))

(defn goal-line
  "A dashed reference line at `goal` across the value axis, labeled with `label`.
   `horizontal?` charts carry values on x, so the line runs vertically."
  [{:keys [goal goal-config label horizontal?]}]
  (let [stroke (or (:color goal-config)
                   (get goal-config "color")
                   (style/alc-color "Text/primary"))]
    [re-reference-line
     (merge (if horizontal? {:x goal} {:y goal})
            {:stroke stroke
             :strokeWidth 1.5
             :strokeDasharray "6 4"
             :ifOverflow "extendDomain"
             :label {:value label
                     :position (if horizontal? "top" "right")
                     :fill stroke
                     :fontSize 11
                     :fontFamily style/roboto-font-family}})]))

(defn axis-max [axis-config]
  (let [m (:max axis-config)]
    (when (number? m) m)))

(defn strip-axis-options
  "An axis config without the keys the charts and ticks read themselves."
  [axis-config]
  (dissoc axis-config :max :percent?))

(defn axis-props
  "`axis-config` as recharts axis props: a `:max` pins the domain's top, clipping data
   past it rather than stretching the axis."
  [axis-config]
  (let [m (axis-max axis-config)]
    (cond-> (strip-axis-options axis-config)
            m (-> (update :domain #(vector (or (first %) 0) m))
                  (assoc :allowDataOverflow true)))))

(defn pivot-series
  "For charts whose series are the distinct values of a data column (one line/area per
   category) rather than named columns. When `series-key` is set and `y-keys` is empty,
   pivots long rows (one row per `x-key` + `series-key` value) into wide rows (one row per
   `x-key` with a column per series value, holding `value-key`) and returns `[wide-data
   series-vals]` — one y-key per distinct series value. Row order is preserved so the x-axis
   stays as the query sorted it. Otherwise returns `[data y-keys]` unchanged."
  [{:keys [data y-keys series-key x-key value-key]}]
  (if (and series-key (not y-keys) (not-empty data))
    (let [series-vals (->> data (map #(get % series-key)) (distinct) (vec))
          wide (->> data
                    (reduce (fn [acc r]
                              (let [x (get r x-key)
                                    i (get-in acc [:idx x])]
                                (if i
                                  (assoc-in acc [:rows i (get r series-key)] (get r value-key))
                                  (-> acc
                                      (update :rows conj {x-key x
                                                          (get r series-key) (get r value-key)})
                                      (assoc-in [:idx x] (count (:rows acc)))))))
                            {:rows [] :idx {}})
                    :rows)]
      [wide series-vals])
    [data y-keys]))

(defn standalone-legend
  "Renders a standalone legend centered under a chart (boxed items, card styling). Shared across
   the recharts components. `entries` is a vector of `{:label :color}` maps — an entry with
   `:shape :line` gets a line swatch instead of a block; an optional `display-fn` transforms each
   label for display (the raw label stays the react key)."
  [{:keys [entries display-fn container-style]}]
  (let [{:keys [chip swatch text]} (legend-item-styles)]
    (into [:div {:style (merge {:display :flex
                                :flex-wrap :wrap
                                :justify-content :center
                                :align-items :center
                                :gap 12
                                :padding 10
                                :width "100%"}
                               container-style)}]
          (mapv (fn [{:keys [label color shape]}]
                  [:div {:key label
                         :style chip}
                   [:div {:style (cond-> (assoc swatch :background-color color)
                                         (= shape :line)
                                         (assoc :height 3))}]
                   [:span {:style text}
                    (if display-fn (display-fn label) label)]])
                entries))))
